import React, { FormEvent, useContext } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import { LocaleContext } from 'contexts/locale'
import { SubmissionStatus } from 'types/submissionStatus'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { VerticalSegment } from 'components/verticalSegment'
import questionList from 'components/questions/questions.yml'

interface SubmitFormProps {
  emailAddress: string
  firstName: string
  lastName: string
  confirmedTerms: boolean
  isComplete: boolean
  submissionStatus: SubmissionStatus
  firebaseErrorCode?: string
}

interface SubmitFormHandlers {
  setEMailAddress: (address: string) => void
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  toggleTerms: () => void
  submit: () => void
}

export function SubmitForm({
  emailAddress = '',
  firstName = '',
  lastName = '',
  confirmedTerms = false,
  isComplete,
  submissionStatus,
  firebaseErrorCode,
  submit,
  setEMailAddress,
  setFirstName,
  setLastName,
  toggleTerms,
  selectedAnswers
}: SubmitFormProps & SubmitFormHandlers) {
  if (submissionStatus === SubmissionStatus.delivered) {
    return null
  }

  const locale = useContext(LocaleContext)
  const messages = require('./locales/' + locale + '.yml')

  const formSubmission = (event: FormEvent) => {
    event.preventDefault()
    if (isComplete) {
      submit()
    }
  }

  const termsLink = `https://abo.${
    parseHostname().publicationDomain
  }/tamstorefront/terms/register`

  let firstUnansweredQuestion: string | null

  if (selectedAnswers) {
    const firstNotAnsweredQuestion = Object.entries(selectedAnswers).find(
      ([id, value]) => {
        // In cases where we get null, the case is clear anyway
        if (!value) {
          return true
        }

        // get question properties
        const properties = questionList.find(q => q.id === id)
        if (properties) {
          if (typeof properties.selectionType === 'number') {
            if ((value as Set<string>).size < properties.selectionType) {
              return true
            }
          }
        }

        return false
      }
    )

    if (firstNotAnsweredQuestion) {
      firstUnansweredQuestion = firstNotAnsweredQuestion[0]
    } else {
      firstUnansweredQuestion = null
    }
  } else {
    firstUnansweredQuestion = 'allParties'
  }

  return (
    <VerticalSegment text className='submitForm grey'>
      {!firstUnansweredQuestion && (
        <div
          className='ui vertical very fitted infographic basic segment'
          dangerouslySetInnerHTML={{ __html: messages.formIntroduction }}
        />
      )}

      {firstUnansweredQuestion && (
        <div className='ui warning message'>
          <div className='header'>{messages.questionWarning.header}</div>
          <p>{messages.questionWarning.explanation}</p>
          <p>
            {' '}
            <a className='' href={`#${firstUnansweredQuestion}`}>
              <i className='small angle double up icon' />
              {messages.questionWarning.button}
            </a>
          </p>
        </div>
      )}

      <form
        onSubmit={formSubmission}
        className={`ui  contest-submit ${submissionStatus ===
          SubmissionStatus.error &&
          firebaseErrorCode &&
          'error'} form`}
      >
        <div className='two fields'>
          <div className='field'>
            <label>{messages.firstName}</label>
            <input
              type='text'
              value={firstName}
              placeholder='Trudi'
              onChange={event => setFirstName(event.target.value)}
            />
          </div>
          <div className='field'>
            <label>{messages.lastName}</label>
            <input
              type='text'
              value={lastName}
              placeholder='Meyer-Müller'
              onChange={event => setLastName(event.target.value)}
            />
          </div>
        </div>
        <div className='field'>
          <label>{messages.email}</label>
          <input
            type='email'
            name='email'
            onChange={event => setEMailAddress(event.target.value)}
            value={emailAddress}
            placeholder='trudi@meyer-mueller.ch'
          />
          <div className='ui pointing above label'>{messages.emailRemark}</div>
        </div>
        <div className='field'>
          <div className='ui checkbox'>
            <input
              id='confirmTerms'
              type='checkbox'
              onChange={toggleTerms}
              checked={confirmedTerms}
              tabIndex={0}
              className='hidden'
            />
            <label htmlFor='confirmTerms'>
              {messages.terms} (
              <a href={termsLink} target='_blank' rel='noopener noreferrer'>
                <i className='small external link icon' />
                {messages.readTerms}
              </a>
              )
            </label>
          </div>
        </div>

        <div className='ui small  vertical very fitted secondary basic infographic segment'>
          <div className='content'>
            <div className='ui list'>
              <div className='item'>{messages.noCorrespondence}</div>
              <div className='item'>{messages.rights}</div>
            </div>
          </div>
        </div>

        <div className='ui error message'>
          <div className='content'>
            <div className='header'>{messages.error.header}</div>
            {firebaseErrorCode && (
              <p>{messages.error.reasons[firebaseErrorCode]}</p>
            )}
          </div>
        </div>

        <button
          className={`ui ${submissionStatus === SubmissionStatus.submitting &&
            'loading'} large primary button`}
          type='submit'
          disabled={!isComplete}
        >
          {messages.submit}
        </button>
      </form>
    </VerticalSegment>
  )
}
